import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useAuth } from './context'

/**
 * Re-authenticate over the screen the user was already on.
 *
 * S-D2's session-expiry behaviour, and the plan states it explicitly: the app
 * "re-auths in place rather than dumping to /login". The screen behind stays
 * mounted, so a half-filled settings form survives a session that expired while
 * it was being typed into.
 *
 * Not dismissable. Every request behind it is 401ing, so the only two ways out
 * are signing back in and signing out — and offering "close" would leave a user
 * clicking a dead interface trying to work out why.
 *
 * Two accounts cannot use this dialog, and the reason is the same for both: it
 * asks for a password, and they have none the server would accept.
 *
 *  * A **Google-only identity** (`has_password === false`) has never set one.
 *  * An account with **no email and no mobile** cannot be identified at all —
 *    §4 permits `users.email` to be null for a mobile signup, and while such an
 *    account always has a mobile in practice, the *type* allows neither.
 *
 * Both are sent to `/login`, which is where the other sign-in methods live.
 */
export function SessionExpired(): JSX.Element {
  const { user, adopt, signOut } = useAuth()
  const [password, setPassword] = useState('')

  const login = useMutation((body: { email?: string; mobile?: string; password: string }) =>
    call(() => api.POST('/api/v1/auth/login', { body })),
  )

  const identifier = user?.email ?? user?.mobile ?? null
  const canReauthenticate = Boolean(user?.has_password && identifier)

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!user) return
    const result = await login.run(
      user.email ? { email: user.email, password } : { mobile: user.mobile ?? '', password },
    )
    if (result.ok) {
      setPassword('')
      adopt({ user: result.data.user, session: result.data.session })
    }
  }

  return (
    <Modal
      title="Your session expired"
      dismissable={false}
      onClose={() => {
        /* Deliberately inert — see the note above. */
      }}
      footer={
        <>
          <Button
            tone="quiet"
            onClick={() => {
              void signOut()
            }}
          >
            Sign out
          </Button>
          {canReauthenticate ? (
            <Button tone="primary" type="submit" form="reauth" busy={login.pending}>
              Sign back in
            </Button>
          ) : (
            <a className="ar-btn ar-btn--primary" href="/login">
              Go to sign in
            </a>
          )}
        </>
      }
    >
      {canReauthenticate ? (
        <form
          id="reauth"
          className="ar-stack"
          onSubmit={(event) => {
            void onSubmit(event)
          }}
        >
          <p style={{ marginTop: 0 }}>
            You have been signed out for security. Enter your password to carry on where you left
            off — nothing on the page behind has been lost.
          </p>
          <InlineError failure={login.state.failure} />
          <TextField
            label="Signed in as"
            value={identifier ?? ''}
            readOnly
            autoComplete="username"
          />
          <TextField
            label="Password"
            type="password"
            required
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
              login.reset()
            }}
          />
        </form>
      ) : (
        <p style={{ marginTop: 0 }}>
          You have been signed out for security. This account signs in without a password, so finish
          on the sign-in page.
        </p>
      )}
    </Modal>
  )
}
